import React, { useState, useEffect } from "react";
import { useTheme, getT } from "./ThemeContext";
import bg6 from "./Background images/6.jpg";
import bg8 from "./Background images/8.jpg";

// Floating hexagons: {x,y}=position %, s=scale, d=animation delay
const HEXES = [
  { x: 5,  y: 7,  s: 1.3, d: 0   },
  { x: 18, y: 50, s: 0.8, d: 1.1 },
  { x: 62, y: 10, s: 1.1, d: 0.6 },
  { x: 78, y: 62, s: 1.5, d: 0.4 },
  { x: 42, y: 78, s: 0.7, d: 1.7 },
  { x: 11, y: 76, s: 1.0, d: 0.9 },
  { x: 86, y: 27, s: 0.9, d: 1.4 },
  { x: 50, y: 44, s: 0.6, d: 2.0 },
];

export default function FuturisticBackground() {
  const { isDark } = useTheme();
  const T = getT(isDark);
  const [bgIdx, setBgIdx] = useState(0);

  useEffect(() => {
    const iv = setInterval(() => setBgIdx(p => (p + 1) % 2), 10000);
    return () => clearInterval(iv);
  }, []);

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: -1,
      pointerEvents: "none", overflow: "hidden",
      background: T.pageBg, transition: "background 0.5s ease",
    }}>
      <style>{`
        @keyframes global_hex {
          0%,100% { transform: translate(-50%,-50%) translateY(0)   rotate(0deg);  opacity: 0.18; }
          50%      { transform: translate(-50%,-50%) translateY(-14px) rotate(4deg); opacity: 0.38; }
        }
        @keyframes global_scan {
          0%   { transform: translateY(-100%); }
          100% { transform: translateY(100dvh); }
        }
      `}</style>

      {/* Cricket-field background images (cross-fade every 10 s) */}
      {[bg6, bg8].map((src, i) => (
        <div key={i} style={{
          position: "absolute", inset: 0,
          backgroundImage: `url(${src})`,
          backgroundSize: "cover", backgroundPosition: "center 25%",
          opacity: bgIdx === i ? (isDark ? 0.55 : 0.40) : 0,
          transition: "opacity 2s ease-in-out",
        }} />
      ))}

      {/* Tinted gradient overlay — lighter in dark so image shows through */}
      <div style={{
        position: "absolute", inset: 0,
        background: isDark
          ? "linear-gradient(135deg, rgba(2,13,24,0.70) 0%, rgba(2,13,24,0.60) 100%)"
          : "linear-gradient(135deg, rgba(255,255,255,0.82) 0%, rgba(255,255,255,0.72) 100%)",
      }} />

      {/* Floating hexagons */}
      {HEXES.map((h, i) => {
        const size = Math.round(58 * h.s);
        return (
          <div key={i} style={{
            position: "absolute",
            left: `${h.x}%`,
            top:  `${h.y}%`,
            width:  size,
            height: size,
            transform: "translate(-50%,-50%)",
            animation: `global_hex ${3.8 + h.d * 0.7}s ease-in-out infinite`,
            animationDelay: `${h.d}s`,
          }}>
            <svg viewBox="0 0 60 60" width={size} height={size} style={{ display:"block" }}>
              <polygon
                points="30,3 56,17 56,43 30,57 4,43 4,17"
                fill="none"
                stroke={isDark ? "rgba(247,206,11,0.55)" : "rgba(3,78,123,0.30)"}
                strokeWidth="1.6"
              />
              {/* Inner ring for depth */}
              <polygon
                points="30,11 48,21 48,39 30,49 12,39 12,21"
                fill="none"
                stroke={isDark ? "rgba(247,206,11,0.20)" : "rgba(3,78,123,0.12)"}
                strokeWidth="0.8"
              />
            </svg>
          </div>
        );
      })}

      {/* Subtle horizontal scan line */}
      <div style={{
        position: "absolute", left: 0, right: 0, height: 2,
        background: isDark ? "rgba(247,206,11,0.06)" : "rgba(3,78,123,0.03)",
        animation: "global_scan 12s linear infinite",
      }} />

      {/* Fine grid lines */}
      <div style={{
        position: "absolute", inset: 0,
        backgroundImage: isDark
          ? "repeating-linear-gradient(0deg,transparent,transparent 2px,rgba(255,255,255,0.015) 2px,rgba(255,255,255,0.015) 4px)"
          : "repeating-linear-gradient(0deg,transparent,transparent 2px,rgba(0,0,0,0.008) 2px,rgba(0,0,0,0.008) 4px)",
      }} />
    </div>
  );
}
